/**
 * OzelIzinlerPage.jsx — Proje Özel İzinleri (denetmen tepsisi)
 * Route: /Management/Projects/SpecialPermits
 * Roller: [1,2,3]
 * Menüde yok (2026-09-15) — Projeler listesinin (StandListPage) araç çubuğundaki
 * "Özel İzinler" düğmesinden açılıyor; buradan geri dönüş düğmesi var.
 *
 * Kaynak: docs/guides/FE-ProjectController-ozel-izin-guide.md (2026-08-28)
 *
 * Bir Proje Özel İzni = "bu fuarın kurallarını ÇİĞNEYEN bir beyanı yine de kaydet"
 * izni. Öncesinde ihlalli beyan reddediliyor ve HİÇBİR ŞEY saklanmıyordu; 5,00 m'lik
 * salonda gerçekten 5,50 m'ye ihtiyacı olan katılımcının talebini koyacağı bir yer
 * yoktu, pazarlık telefonda yapılıyordu.
 *
 * BU BİR LİSTE DEĞİL KUYRUKTUR — en uzun bekleyen en üstte, çünkü o projede hiçbir
 * şey kımıldayamaz: bekleyen bir izin varken `assign-system` beyanı KABUL ETMİYOR.
 * (Bu kasıtlı: aksi halde izin yolu sıraya kaynak yapmanın yolu olurdu.)
 *
 * ⚠️ TEPSİ SATIRLARI KOD TAŞIR, MESAJ DEĞİL. `ihlalKodlari` makine kodudur ve asla
 *    katılımcıya gösterilmez; insan cümlelerini ve gerçek sayıları görmek için
 *    projeyi çözmek gerekir (GET /projects/by-stand/{standId} → projeBeyani.violations).
 *    Sayfa bunu satır açıldığında yapıyor, listede N+1 olmasın diye peşin değil.
 *
 * ⚠️ YETKİ BACKEND'DE ZORLANMIYOR (guide §8.2). `canDecide` bugün sadece "oturum var
 *    mı" diye bakıyor — teknik olarak talebi açan katılımcı kendi iznini onaylayabilir.
 *    Bu sayfa yalnızca denetmen rollerine açık ([1,2,3]) ve katılımcı tarafında bir
 *    Onayla düğmesi YOK; backend'in koruduğunu varsaymıyoruz.
 *
 * ⚠️ `person_single_permit` ile KARIŞTIRMAYIN — o da UI'da "Özel İzin" diye geçiyor
 *    ama bir KİŞİYİ belgeleri olmadan kapıdan geçiriyor. Bu ekran PROJELERLE ilgili.
 */

import React, { useContext, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Row, Col,
  Button, Badge, Table, Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Spinner, Input, FormGroup, Label,
} from "reactstrap";
import {
  ShieldCheck, ShieldX, RefreshCw, AlertTriangle, Info, Clock, User, FileText, ArrowLeft,
} from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import KuralIhlalListesi from "../../../Components/Projects/KuralIhlalListesi";
import {
  getBekleyenOzelIzinler, decideOzelIzin, getProjectByStandId,
  OZEL_IZIN_DURUM, OZEL_IZIN_NOT_MAX, getAcceptedHeight,
} from "../../../services/projectService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

// WAS (until #398): only the two seeded test fairs ran on the real backend, every other fair fell back to mock data:
// const LIVE_TEST_FUAR_IDS = [298, 299];
// const isLiveTestFuarId = (fuarId) => LIVE_TEST_FUAR_IDS.includes(Number(fuarId));
// #398: a full-simulation test creates a NEW fair, so every real fair id is live.
const isLiveTestFuarId = (fuarId) => Number(fuarId) > 0;

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(currentLocaleTag());
};

/** "3 gün 4 saat" — tepsinin tek gerçek önceliklendirme sinyali. */
function bekleme(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const saat = Math.floor(ms / 3600000);
  const gun = Math.floor(saat / 24);
  // WAS: if (gun >= 1) return `${gun} gün ${saat % 24} saat`;
  // WAS: if (saat >= 1) return `${saat} saat`;
  // WAS: return `${Math.max(1, Math.floor(ms / 60000))} dk`;
  // #349: the units come from the dictionary.
  if (gun >= 1) return i18n.t("projeler:ozelIzinler.beklemeGunSaat", { gun, saat: saat % 24 });
  if (saat >= 1) return i18n.t("projeler:ozelIzinler.beklemeSaat", { saat });
  return i18n.t("projeler:ozelIzinler.beklemeDakika", { dk: Math.max(1, Math.floor(ms / 60000)) });
}

/** #349: waiting a day or more turns the badge red. WAS: bekleme(...)?.includes("gün"), a test on the Turkish text. */
function birGundenFazla(iso) {
  if (!iso) return false;
  const ms = Date.now() - new Date(iso).getTime();
  return !Number.isNaN(ms) && ms >= 24 * 3600000;
}

export default function OzelIzinlerPage() {
  const { t: tx } = useTranslation("projeler");
  const { selectedFuarId } = useContext(MainContext);
  const isLive = isLiveTestFuarId(selectedFuarId);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Açılan satırın projesi — ihlal CÜMLELERİ ancak buradan geliyor.
  const [detail, setDetail] = useState(null);      // { izin, project, loading, error }
  const [decision, setDecision] = useState(null);  // { izin, onay }
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = i18n.t("projeler:ozelIzinler.projeOzelIzinleriFds"); }, []);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  useEffect(() => {
    if (!selectedFuarId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const list = await getBekleyenOzelIzinler(selectedFuarId, { forceLive: isLive });
        if (!ignore) setRows(list || []);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [selectedFuarId, isLive, reloadToken]);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  // Satır açılınca projeyi çöz — ihlal mesajlarını ve beyan edilen sayıları göstermek
  // için tek yol bu, ve listede peşin yapmak stand başına bir istek demek olurdu.
  async function openDetail(izin) {
    setDetail({ izin, project: null, loading: true, error: null });
    try {
      const p = await getProjectByStandId(izin.standId, { forceLive: isLive });
      setDetail({ izin, project: p, loading: false, error: p ? null : tx("ozelIzinler.buStandinProjesiBulunamadi") });
    } catch (e) {
      setDetail({ izin, project: null, loading: false, error: e.message });
    }
  }

  function openDecision(izin, onay) {
    setDecision({ izin, onay });
    setNote("");
  }

  async function submitDecision() {
    if (!decision) return;
    // Ret gerekçesi backend'de de zorunlu ama oradaki reddi UX olarak kullanmıyoruz:
    // gerekçesiz "hayır", katılımcıya üzerinde çalışacak hiçbir şey bırakmaz.
    if (!decision.onay && !note.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await decideOzelIzin(decision.izin.id, decision.onay, note.trim() || null, { forceLive: isLive });
      setSuccessMsg(decision.onay
        ? tx("ozelIzinler.ozelIzinOnaylandiBeyandakiIhlaller")
        : tx("ozelIzinler.ozelIzinReddedildiProjeKilitli"));
      setDecision(null);
      setDetail(null);
      refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!selectedFuarId) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={tx("ozelIzinler.projeOzelIzinleri")} pageTitle={tx("ozelIzinler.projeler")} />
          <Alert color="secondary">{tx("ozelIzinler.bekleyenTalepleriGormekIcinOnce")}</Alert>
        </Container>
      </div>
    );
  }

  const pb = detail?.project?.projeBeyani ?? null;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("ozelIzinler.projeOzelIzinleri")} pageTitle={tx("ozelIzinler.projeler")} />

        {error && <Alert color="danger">{error}</Alert>}
        {successMsg && <Alert color="success">{successMsg}</Alert>}

        <Card className="mb-3">
          <CardBody className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="text-muted small d-flex align-items-start gap-2" style={{ maxWidth: 900 }}>
              <Info size={15} className="flex-shrink-0 mt-1" />
              <span>
                {tx("ozelIzinler.katilimciFuarKurallariniCigneyenBir")}{" "}<span className="fw-semibold">{tx("ozelIzinler.kaydettirmek")}</span>{" "}{tx("ozelIzinler.icinIzinIstediTalepKarara")}{" "}<span className="fw-semibold">{tx("ozelIzinler.kilitli")}</span>{" "}{tx("ozelIzinler.kalirVeBeyanKabulEdilemez")}
              </span>
            </div>
            <div className="d-flex gap-2">
              <Link to="/Management/Projects" className="btn btn-light btn-sm border">
                <ArrowLeft size={14} className="me-1" />{tx("ozelIzinler.projeler")}
              </Link>
              <Button color="secondary" outline size="sm" onClick={refresh} disabled={loading}>
                <RefreshCw size={14} className="me-1" />{tx("common:yenile")}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="d-flex align-items-center gap-2">
            <ShieldCheck size={16} className="text-warning" />
            <span className="fw-semibold">{tx("ozelIzinler.onayBekleyenTalepler")}</span>
            <Badge color={rows.length ? "warning" : "light"} className={rows.length ? "" : "text-dark"}>
              {rows.length}
            </Badge>
            <span className="text-muted small ms-2">{tx("ozelIzinler.enUzunBekleyenUstte")}</span>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : rows.length === 0 ? (
              <Alert color="success" className="mb-0">
                {tx("ozelIzinler.buFuardaOnayBekleyenOzel")}
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle table-nowrap mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 70 }}>#</th>
                      <th>{tx("ozelIzinler.stand")}</th>
                      <th>{tx("ozelIzinler.talepEden")}</th>
                      <th style={{ width: 150 }}>{tx("ozelIzinler.bekleme")}</th>
                      <th>{tx("ozelIzinler.gerekce")}</th>
                      <th style={{ width: 230 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="text-muted">{r.id}</td>
                        <td className="fw-semibold">
                          <Link to={`/Management/Projects/Stands/${r.standId}`}>#{r.standId}</Link>
                          {/* Kodlar sadece "ne istendi"nin özeti; cümleler detayda. */}
                          {r.ihlalKodlari?.length > 0 && (
                            <div className="text-muted fw-normal small">
                              {tx("ozelIzinler.madde", { length: r.ihlalKodlari.length })}
                            </div>
                          )}
                        </td>
                        <td>
                          <User size={12} className="me-1 text-muted" />
                          {r.talepEdenAdi ?? <span className="text-muted">#{r.talepEden}</span>}
                          <div className="text-muted small">{fmtDateTime(r.talepTarihi)}</div>
                        </td>
                        <td>
                          {/* WAS: <Badge color={bekleme(r.talepTarihi)?.includes("gün") ? "danger" : "secondary"}> */}
                          <Badge color={birGundenFazla(r.talepTarihi) ? "danger" : "secondary"}>
                            <Clock size={11} className="me-1" />{bekleme(r.talepTarihi) ?? "—"}
                          </Badge>
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 260 }}>
                          {r.talepNotu
                            ? <span title={r.talepNotu}>{r.talepNotu}</span>
                            : <span className="text-muted small">{tx("ozelIzinler.gerekceYazilmamis")}</span>}
                        </td>
                        <td>
                          <Button color="secondary" outline size="sm" className="me-1" onClick={() => openDetail(r)}>
                            <FileText size={13} className="me-1" />{tx("ozelIzinler.incele")}
                          </Button>
                          <Button color="success" outline size="sm" className="me-1" onClick={() => openDecision(r, true)}>
                            <ShieldCheck size={13} />
                          </Button>
                          <Button color="danger" outline size="sm" onClick={() => openDecision(r, false)}>
                            <ShieldX size={13} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        {/* ─── İnceleme: gerçek cümleler ve sayılar ─── */}
        <Modal isOpen={Boolean(detail)} toggle={() => !busy && setDetail(null)} centered size="lg">
          <ModalHeader toggle={() => !busy && setDetail(null)}>
            {tx("ozelIzinler.ozelIzinTalebiStand", { id: detail?.izin?.id, standId: detail?.izin?.standId })}
          </ModalHeader>
          <ModalBody>
            {detail?.loading && <div className="text-center py-4"><Spinner /></div>}
            {detail?.error && <Alert color="warning">{detail.error}</Alert>}

            {detail?.izin && (
              <Card className="border mb-3">
                <CardBody className="py-2">
                  <Row className="g-2 small">
                    <Col md={6}>
                      <span className="text-muted">{tx("ozelIzinler.talepEden2")}</span>{" "}
                      <span className="fw-semibold">{detail.izin.talepEdenAdi ?? `#${detail.izin.talepEden}`}</span>
                    </Col>
                    <Col md={6}>
                      <span className="text-muted">{tx("ozelIzinler.talepTarihi")}</span>{" "}
                      <span className="fw-semibold">{fmtDateTime(detail.izin.talepTarihi)}</span>
                    </Col>
                    <Col xs={12}>
                      <span className="text-muted">{tx("ozelIzinler.gerekce2")}</span>{" "}
                      {detail.izin.talepNotu ?? <span className="text-muted">{tx("ozelIzinler.yazilmamis")}</span>}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}

            {pb && (
              <>
                <div className="fw-semibold mb-2">{tx("ozelIzinler.beyanEdilen")}</div>
                <Row className="g-2 small mb-3">
                  <Col md={4}>
                    <span className="text-muted">{tx("ozelIzinler.standTipi")}</span>{" "}
                    <span className="fw-semibold">{pb.declaredStandTypeName ?? "—"}</span>
                  </Col>
                  <Col md={4}>
                    <span className="text-muted">{tx("ozelIzinler.yukseklik")}</span>{" "}
                    <span className="fw-semibold">
                      {pb.declaredHeightNumber == null ? "—" : `${String(pb.declaredHeightNumber).replace(".", ",")} m`}
                    </span>
                  </Col>
                  <Col md={4}>
                    <span className="text-muted">{tx("ozelIzinler.alan")}</span>{" "}
                    <span className="fw-semibold">
                      {pb.declaredAreaM2 == null ? "—" : `${String(pb.declaredAreaM2).replace(".", ",")} m²`}
                    </span>
                  </Col>
                  <Col md={4}>
                    <span className="text-muted">{tx("ozelIzinler.gecerliTavan")}</span>{" "}
                    <span className="fw-semibold">
                      {pb.effectiveHMax == null ? tx("ozelIzinler.kayitYok") : `${String(pb.effectiveHMax).replace(".", ",")} m`}
                    </span>
                  </Col>
                  <Col md={4}>
                    <span className="text-muted">{tx("ozelIzinler.elektrik")}</span>{" "}
                    <span className="fw-semibold">{pb.declaredKw == null ? "—" : `${pb.declaredKw} kW`}</span>
                  </Col>
                  <Col md={4}>
                    <span className="text-muted">{tx("ozelIzinler.kabulEdilenYukseklik")}</span>{" "}
                    <span className="fw-semibold">{getAcceptedHeight(pb) ?? "—"}</span>
                  </Col>
                </Row>

                {/* İhlallerin İNSAN CÜMLELERİ — tepsi satırındaki kodların karşılığı. */}
                <KuralIhlalListesi
                  violations={pb.violations}
                  title={tx("ozelIzinler.buBeyandakiKuralIhlalleri")}
                />

                {/* Bir izin BEYANIN TAMAMINI kapsar; ihlalKodlari talep anındaki
                    fotoğraftır, filtre değil. Onay sonradan çıkan izin verilebilir
                    ihlalleri de temizler. */}
                <Alert color="light" className="border small mb-0">
                  <Info size={14} className="me-1" />
                  {tx("ozelIzinler.onaylarsanizBuIzin")}{" "}<span className="fw-semibold">{tx("ozelIzinler.beyaninTamamini")}</span>{" "}{tx("ozelIzinler.kapsarTalepAnindakiMaddeleriDegil")}
                  <span className="fw-semibold">{tx("ozelIzinler.izinVerilebilirHerIhlali")}</span>{tx("ozelIzinler.katilimciYeniBirBeyanVerirse")}
                </Alert>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setDetail(null)} disabled={busy}>{tx("common:kapat")}</Button>
            <Button color="danger" outline onClick={() => openDecision(detail.izin, false)} disabled={busy}>
              <ShieldX size={14} className="me-1" />{tx("ozelIzinler.reddet")}
            </Button>
            <Button color="success" onClick={() => openDecision(detail.izin, true)} disabled={busy}>
              <ShieldCheck size={14} className="me-1" />{tx("ozelIzinler.onayla")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ─── Karar ─── */}
        <Modal isOpen={Boolean(decision)} toggle={() => !busy && setDecision(null)} centered>
          <ModalHeader toggle={() => !busy && setDecision(null)}>
            {decision?.onay ? tx("ozelIzinler.ozelIzniOnayla") : tx("ozelIzinler.ozelIzniReddet")}
          </ModalHeader>
          <ModalBody>
            {decision?.onay ? (
              <Alert color="warning" className="small">
                <AlertTriangle size={15} className="me-1" />
                {tx("ozelIzinler.ihlaller")}{" "}<span className="fw-semibold">{tx("ozelIzinler.listedenSilinmez")}</span>{" "}{tx("ozelIzinler.ozelIzinleKabulEdilenOlarak")}
              </Alert>
            ) : (
              <Alert color="secondary" className="small">
                {tx("ozelIzinler.retSonrasiProje")}{" "}<span className="fw-semibold">{tx("ozelIzinler.kilitliKalir")}</span>{tx("ozelIzinler.katilimciKurallaraUyanYeniBir")}
              </Alert>
            )}

            <FormGroup className="mb-0">
              <Label size="sm">
                {tx("ozelIzinler.gerekce")}{" "}{decision?.onay
                  ? <span className="text-muted">{tx("ozelIzinler.istegeBagli")}</span>
                  : <span className="text-danger">{tx("ozelIzinler.zorunlu")}</span>}
              </Label>
              <Input
                type="textarea" rows={4} value={note} maxLength={OZEL_IZIN_NOT_MAX}
                onChange={(e) => setNote(e.target.value)}
                placeholder={decision?.onay
                  ? tx("ozelIzinler.ornOrganizatorOnayladi550")
                  : tx("ozelIzinler.ornSalonTavaniBunaElvermiyor")}
              />
              <div className="text-muted small mt-1">
                {!decision?.onay && (
                  <>{tx("ozelIzinler.katilimcinin")}{" "}<span className="fw-semibold">{tx("ozelIzinler.niyeReddedildiginiOkuyacagiTekYer")}</span>{" "}{tx("ozelIzinler.burasidir")}{" "}</>
                )}
                {note.length}/{OZEL_IZIN_NOT_MAX}
              </div>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setDecision(null)} disabled={busy}>{tx("ozelIzinler.vazgec")}</Button>
            <Button
              color={decision?.onay ? "success" : "danger"}
              onClick={submitDecision}
              disabled={busy || (!decision?.onay && !note.trim())}
            >
              {busy ? <Spinner size="sm" className="me-1" />
                : decision?.onay ? <ShieldCheck size={14} className="me-1" /> : <ShieldX size={14} className="me-1" />}
              {decision?.onay ? tx("ozelIzinler.onayla") : tx("ozelIzinler.reddet")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}

/** Tepsi rozeti için — durum etiketlerini tek yerden okumak isteyen sayfalar kullansın. */
export { OZEL_IZIN_DURUM };
